/**
 * Always-on crash breadcrumbs — works in production Electron builds
 * (unlike boot telemetry which is often disabled).
 */

import { kvGet, kvSet } from '../storage/kvAccess';

const LAST_CRASH_KEY = 'aman_last_crash_debug';
const MAX_STACK = 2400;

function trimStack(stack) {
  if (!stack) return '';
  const s = String(stack);
  return s.length > MAX_STACK ? `${s.slice(0, MAX_STACK)}…` : s;
}

/**
 * @param {Error|unknown} error
 * @param {{ componentStack?: string, context?: string }|null} [info]
 */
export function persistLastCrash(error, info = null) {
  try {
    const entry = {
      at: new Date().toISOString(),
      context: info?.context || 'ErrorBoundary',
      name: error?.name || 'Error',
      message: error?.message || String(error || 'Unknown error'),
      stack: trimStack(error?.stack),
      componentStack: trimStack(info?.componentStack)
    };
    kvSet(LAST_CRASH_KEY, entry);
    if (typeof window !== 'undefined') {
      window.__AMAN_LAST_CRASH = entry;
    }
    return entry;
  } catch {
    return null;
  }
}

export function getLastCrash() {
  try {
    if (typeof window !== 'undefined' && window.__AMAN_LAST_CRASH) {
      return window.__AMAN_LAST_CRASH;
    }
    return kvGet(LAST_CRASH_KEY) || null;
  } catch {
    return null;
  }
}

export function getRecentErrorLogEntries(limit = 5) {
  try {
    const log = kvGet('error_log');
    if (!Array.isArray(log) || log.length === 0) return [];
    return log.slice(-Math.max(1, limit)).reverse();
  } catch {
    return [];
  }
}

/**
 * Lightweight boot snapshot even when full telemetry is off.
 */
export function getBootDebugSnapshot() {
  try {
    const api = typeof window !== 'undefined' ? window.__AMAN_BOOT_TELEMETRY : null;
    if (api?.export) {
      const full = api.export();
      const marks = full?.marks || {};
      const keys = Object.keys(marks);
      const lastGate = keys.length ? keys[keys.length - 1] : null;
      return {
        enabled: api.isEnabled?.() === true,
        lastGate,
        msToDashboard: full?.summary?.msToDashboardMount ?? null,
        idbReads: full?.summary?.idbReads ?? null,
        idbWrites: full?.summary?.idbWrites ?? null,
        gates: keys.slice(-8)
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function formatCrashDebugText({ includeBoot = true } = {}) {
  const crash = getLastCrash();
  const recent = getRecentErrorLogEntries(3);
  const boot = includeBoot ? getBootDebugSnapshot() : null;
  const lines = [];

  if (crash) {
    lines.push(`[Crash] ${crash.at || ''}`);
    lines.push(`${crash.name}: ${crash.message}`);
    if (crash.stack) lines.push(crash.stack);
    if (crash.componentStack) lines.push(`Component:\n${crash.componentStack}`);
  } else {
    lines.push('[Crash] aucun enregistré');
  }

  if (recent.length) {
    lines.push('—');
    lines.push('[error_log récents]');
    for (const e of recent) {
      lines.push(
        `${e.timestamp || ''} [${e.context || '?'}] ${e.error?.message || e.message || ''}`
      );
    }
  }

  if (boot) {
    lines.push('—');
    lines.push(
      `[Boot] lastGate=${boot.lastGate || '—'} dashboardMs=${boot.msToDashboard ?? '—'} idb=${boot.idbReads ?? 0}/${boot.idbWrites ?? 0}`
    );
    if (boot.gates?.length) lines.push(`gates: ${boot.gates.join(' → ')}`);
  }

  return lines.join('\n');
}

export function installCrashDebugGlobal() {
  if (typeof window === 'undefined') return;
  window.__AMAN_CRASH_DEBUG = {
    getLastCrash,
    getRecentErrorLogEntries,
    getBootDebugSnapshot,
    formatCrashDebugText,
    persistLastCrash
  };
}
